import { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Loader2, 
  Upload, 
  FileArchive, 
  CheckCircle2, 
  XCircle,
  ArrowLeft,
  Copy,
  Download,
  AlertCircle,
  FileText,
  Users,
  ExternalLink
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  { value: "usa", label: "USA" },
  { value: "canada", label: "Canada" },
  { value: "jamaica", label: "Jamaica" },
  { value: "barbados", label: "Barbados" },
  { value: "trinidad_tobago", label: "Trinidad & Tobago" },
  { value: "panama", label: "Panama" }
];

const ASSESSMENT_TYPES = [
  { value: "self_assessment", label: "Self Assessment" },
  { value: "technical", label: "Technical Assessment" },
  { value: "manager_validation", label: "Manager Validation" },
  { value: "simulation", label: "Hands-On Simulation" }
];

export default function AdminBulkUpload() {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedAssessmentType, setSelectedAssessmentType] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);

  const getAuthHeaders = () => ({
    headers: { 
      Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
    }
  });

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect({ target: { files } });
    }
  }, []);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.zip')) {
      toast.error("Please select a .zip file");
      return;
    }

    setSelectedFile(file);
    setUploadResults(null);
    toast.success(`ZIP file "${file.name}" selected. Please choose region and assessment type.`);
  };

  const handleBulkUpload = async () => {
    if (!selectedFile || !selectedRegion || !selectedAssessmentType) {
      toast.error("Please select a ZIP file, region, and assessment type");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("region", selectedRegion);
      formData.append("assessment_type", selectedAssessmentType);
      
      const response = await axios.post(
        `${API}/admin/bulk-upload-zip`,
        formData,
        {
          headers: {
            ...getAuthHeaders().headers,
            "Content-Type": "multipart/form-data"
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );
      
      setUploadResults(response.data);
      setSelectedFile(null);
      
      if (response.data.summary.successful > 0) {
        toast.success(`Successfully uploaded ${response.data.summary.successful} assessments!`);
      }
      if (response.data.summary.failed > 0) {
        toast.error(`${response.data.summary.failed} files failed to process`);
      }
    } catch (err) {
      console.error("Upload error:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/admin/login");
      } else {
        toast.error(err.response?.data?.detail || "Bulk upload failed");
      }
    } finally {
      setUploading(false);
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/assessment/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Assessment link copied!");
  };

  const copyAllLinks = () => {
    if (!uploadResults?.results) return;
    const links = uploadResults.results.map(r => 
      `${r.fullName}\t${r.email}\t${window.location.origin}/assessment/${r.uniqueToken}`
    ).join('\n');
    navigator.clipboard.writeText(links);
    toast.success("All assessment links copied!");
  };

  const downloadReport = () => {
    if (!uploadResults) return;
    
    // Create CSV content
    let csv = "Name,Email,Assessment ID,Assessment URL,Talent Password,Manager,Manager Email,Manager Password,Status\n";
    
    uploadResults.results?.forEach(r => {
      csv += `"${r.fullName}","${r.email}","${r.assessmentId}","${window.location.origin}${r.assessmentUrl}","${r.talentAccount?.temporaryPassword || 'Existing'}","${r.managerAccount?.name || ''}","${r.managerAccount?.email || ''}","${r.managerAccount?.temporaryPassword || 'Existing'}","Success"\n`;
    });
    
    uploadResults.errors?.forEach(e => {
      csv += `"${e.filename}","","","","","","","","Failed: ${e.error}"\n`;
    });
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-upload-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Report downloaded!");
  };

  const getRegionLabel = (value) => {
    return REGIONS.find(r => r.value === value)?.label || value;
  };

  const getTypeLabel = (value) => {
    return ASSESSMENT_TYPES.find(t => t.value === value)?.label || value;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky-header py-4 px-6 bg-white">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/admin" 
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Dashboard</span>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#003366] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">S</span>
            </div>
            <span className="text-lg font-bold text-[#003366] font-['Manrope'] hidden sm:inline">
              Bulk Upload
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto py-8 px-6">
        {/* Upload Zone */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-[#003366] flex items-center gap-2">
              <FileArchive className="w-6 h-6" />
              Bulk ZIP Upload
            </CardTitle>
            <CardDescription>
              Upload a ZIP file containing multiple .docx assessment files. All files will be processed automatically.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* File Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                dragOver ? 'border-[#003366] bg-[#003366]/5' : 
                selectedFile ? 'border-green-500 bg-green-50' : 
                'border-slate-300 hover:border-[#003366]/50'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !selectedFile && document.getElementById('zip-input').click()}
              data-testid="bulk-upload-zone"
            >
              {selectedFile ? (
                <div className="space-y-2">
                  <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
                  <p className="text-lg font-medium text-green-700">{selectedFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setUploadResults(null);
                    }}
                    className="mt-2"
                  >
                    Choose Different File
                  </Button>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 bg-[#003366]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileArchive className="w-8 h-8 text-[#003366]" />
                  </div>
                  <p className="text-lg font-medium text-[#003366] mb-2">
                    Drag & drop your ZIP file here
                  </p>
                  <p className="text-muted-foreground mb-4">or click to browse</p>
                  <p className="text-xs text-muted-foreground">
                    ZIP should contain .docx assessment files
                  </p>
                </>
              )}
            </div>
            <input
              id="zip-input"
              type="file"
              accept=".zip"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="zip-input"
            />

            {/* Region and Assessment Type Selection */}
            {selectedFile && (
              <div className="space-y-4 pt-4 border-t animate-fade-in">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Region Dropdown */}
                  <div className="space-y-2">
                    <Label htmlFor="region" className="text-sm font-medium">
                      Region <span className="text-red-500">*</span>
                    </Label>
                    <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                      <SelectTrigger id="region" data-testid="bulk-region-select">
                        <SelectValue placeholder="Select region for all files" />
                      </SelectTrigger>
                      <SelectContent>
                        {REGIONS.map((region) => (
                          <SelectItem key={region.value} value={region.value}>
                            {region.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Assessment Type Dropdown */}
                  <div className="space-y-2">
                    <Label htmlFor="assessment-type" className="text-sm font-medium">
                      Assessment Type <span className="text-red-500">*</span>
                    </Label>
                    <Select value={selectedAssessmentType} onValueChange={setSelectedAssessmentType}>
                      <SelectTrigger id="assessment-type" data-testid="bulk-assessment-type-select">
                        <SelectValue placeholder="Select assessment type for all files" />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSESSMENT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Upload Button */}
                <Button
                  onClick={handleBulkUpload}
                  disabled={!selectedRegion || !selectedAssessmentType || uploading}
                  className="w-full h-12 bg-[#003366] hover:bg-[#003366]/90 text-white"
                  data-testid="bulk-upload-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing ZIP File...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload & Process All Files
                    </>
                  )}
                </Button>

                {uploading && (
                  <div className="space-y-2">
                    <Progress value={uploadProgress} className="w-full" />
                    <p className="text-sm text-center text-muted-foreground">
                      {uploadProgress < 100 ? 'Uploading...' : 'Processing files...'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload Results */}
        {uploadResults && (
          <Card data-testid="bulk-upload-results">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-[#003366] flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Upload Results
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {uploadResults.summary.successful} successful, {uploadResults.summary.failed} failed out of {uploadResults.summary.totalFiles} files
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={copyAllLinks}>
                    <Copy className="w-4 h-4 mr-1" />
                    Copy All Links
                  </Button>
                  <Button variant="outline" size="sm" onClick={downloadReport}>
                    <Download className="w-4 h-4 mr-1" />
                    Download Report
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-700">{uploadResults.summary.successful}</p>
                  <p className="text-sm text-green-600">Successful</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-red-700">{uploadResults.summary.failed}</p>
                  <p className="text-sm text-red-600">Failed</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-blue-700">{uploadResults.summary.totalFiles}</p>
                  <p className="text-sm text-blue-600">Total Files</p>
                </div>
              </div>

              {/* Results Table */}
              {uploadResults.results?.length > 0 && (
                <div className="border rounded-lg overflow-hidden mb-6">
                  <div className="max-h-[500px] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-slate-100">
                        <TableRow>
                          <TableHead className="w-[50px]">#</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Assessment ID</TableHead>
                          <TableHead>Temp Password</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {uploadResults.results.map((result, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{index + 1}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                                {result.fullName}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {result.email}
                            </TableCell>
                            <TableCell>
                              <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">
                                {result.assessmentId}
                              </code>
                            </TableCell>
                            <TableCell>
                              {result.talentAccount?.temporaryPassword ? (
                                <code className="bg-amber-50 border border-amber-200 px-2 py-0.5 rounded text-xs">
                                  {result.talentAccount.temporaryPassword}
                                </code>
                              ) : (
                                <span className="text-xs text-muted-foreground">Existing</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyLink(result.uniqueToken)}
                                  className="h-8 px-2"
                                >
                                  <Copy className="w-3 h-3" />
                                </Button>
                                <Link to={result.assessmentUrl} target="_blank">
                                  <Button variant="ghost" size="sm" className="h-8 px-2">
                                    <ExternalLink className="w-3 h-3" />
                                  </Button>
                                </Link>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Errors Table */}
              {uploadResults.errors?.length > 0 && (
                <div className="border border-red-200 rounded-lg overflow-hidden">
                  <div className="bg-red-50 px-4 py-2 border-b border-red-200">
                    <h4 className="font-medium text-red-700 flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      Failed Files ({uploadResults.errors.length})
                    </h4>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Filename</TableHead>
                          <TableHead>Error</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {uploadResults.errors.map((error, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{error.filename}</TableCell>
                            <TableCell className="text-sm text-red-600">{error.error}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-[#003366] text-lg">How to Use Bulk Upload</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-sm text-muted-foreground list-decimal list-inside">
              <li className="flex items-start gap-2">
                <span className="font-semibold text-[#003366] min-w-[20px]">1.</span>
                <span>Place all your .docx assessment files in a single folder</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold text-[#003366] min-w-[20px]">2.</span>
                <span>Compress the folder into a .zip file</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold text-[#003366] min-w-[20px]">3.</span>
                <span>Upload the .zip file using the form above</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold text-[#003366] min-w-[20px]">4.</span>
                <span>Select the region and assessment type (applies to all files)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold text-[#003366] min-w-[20px]">5.</span>
                <span>Click "Upload & Process All Files"</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold text-[#003366] min-w-[20px]">6.</span>
                <span>Download the report with all assessment links and credentials</span>
              </li>
            </ol>
            
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Important Notes</p>
                  <ul className="mt-1 text-sm text-amber-700 space-y-1">
                    <li>• All files in the ZIP must be .docx format</li>
                    <li>• The same region and assessment type will be applied to all files</li>
                    <li>• Files starting with ~$ (temp files) will be ignored</li>
                    <li>• Subdirectories within the ZIP are supported</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
